/**
 * Compatibility adapter for the Vue app: talks to the hardened /api/v2 checkout contract.
 *
 * Rules kept from the React client so both frontends behave the same:
 * - the guest credential stays in the HttpOnly cookie; only the CSRF token is held in memory
 * - every unsafe request needs an explicit, stable idempotency key
 * - responses are validated at the boundary; nothing trusted comes from the URL or the client
 *
 * This module is deliberately dependency-free so the Vue toolchain does not change.
 */

const V2 = '/api/v2'

const MESSAGES = {
  not_found: 'Không tìm thấy thông tin yêu cầu.',
  unauthenticated: 'Phiên truy cập đã hết hạn.',
  forbidden: 'Không thể thực hiện yêu cầu này.',
  seat_conflict: 'Ghế vừa được chọn. Vui lòng chọn ghế khác.',
  stock_conflict: 'Dịch vụ không còn đủ số lượng. Vui lòng kiểm tra lại.',
  quote_changed: 'Giá vé đã thay đổi. Vui lòng kiểm tra lại.',
  version_conflict: 'Đơn đã thay đổi. Vui lòng tải lại thông tin.',
  idempotency_conflict: 'Yêu cầu không khớp lần thực hiện trước.',
  checkout_expired: 'Thời gian giữ chỗ đã hết.',
  validation_error: 'Vui lòng kiểm tra thông tin đã nhập.',
  rate_limited: 'Vui lòng chờ trước khi thử lại.',
  service_unavailable: 'Chức năng chưa sẵn sàng. Vui lòng thử lại sau.',
  internal_error: 'Không thể xử lý yêu cầu. Vui lòng thử lại.',
  invalid_request: 'Yêu cầu không hợp lệ.',
  invalid_response: 'Phản hồi không hợp lệ. Vui lòng tải lại thông tin.',
  network_error: 'Không kết nối được. Vui lòng kiểm tra mạng.',
}

const STATUS_CODES = {
  401: 'unauthenticated', 403: 'forbidden', 404: 'not_found',
  409: 'version_conflict', 410: 'checkout_expired', 422: 'validation_error',
  429: 'rate_limited', 503: 'service_unavailable',
}

export class CheckoutV2Error extends Error {
  constructor(code, details = {}) {
    super(MESSAGES[code] || MESSAGES.internal_error)
    this.name = 'CheckoutV2Error'
    this.code = MESSAGES[code] ? code : 'internal_error'
    this.status = details.status
    this.uncertain = details.uncertain === true
    this.fieldErrors = details.fieldErrors || {}
    this.requestId = details.requestId
    this.retryAfterMs = details.retryAfterMs
  }
}

function safePath(path) {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || /[\\#\s]/.test(path)) {
    throw new CheckoutV2Error('invalid_request')
  }
  const decoded = decodeURIComponent(path.split('?')[0])
  if (decoded.split('/').some((segment) => segment === '.' || segment === '..')) {
    throw new CheckoutV2Error('invalid_request')
  }
  return `${V2}${path}`
}

function isSafeKey(key) {
  return typeof key === 'string' && key.length >= 1 && key.length <= 128 && /^[\x21-\x7e]+$/.test(key)
}

const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value)

function fieldErrorsFrom(body, allowedFields) {
  const raw = isPlainObject(body) && isPlainObject(body.field_errors) ? body.field_errors : {}
  const result = {}
  for (const field of allowedFields || []) {
    const value = raw[field]
    if (typeof value === 'string' && value.length <= 200) result[field] = value
  }
  return result
}

export function createCheckoutV2(options = {}) {
  const fetchImpl = options.fetch || ((url, init) => fetch(url, init))
  let csrfToken = null
  let bootstrap = null
  let identityEpoch = 0

  function reset() {
    csrfToken = null
    bootstrap = null
    identityEpoch += 1
    if (options.onIdentityInvalidated) options.onIdentityInvalidated()
  }

  async function request(method, path, { body, key, fieldPaths } = {}) {
    const url = safePath(path)
    const unsafe = method !== 'GET'
    if (unsafe && !isSafeKey(key)) throw new CheckoutV2Error('invalid_request')
    let response
    try {
      response = await fetchImpl(url, {
        method,
        credentials: 'same-origin',
        headers: {
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(unsafe ? { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': key } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      })
    } catch (error) {
      throw new CheckoutV2Error('network_error', { uncertain: unsafe })
    }
    let payload = null
    try {
      payload = await response.json()
    } catch {
      if (response.ok) throw new CheckoutV2Error('invalid_response')
    }
    if (response.status === 401) reset()
    if (!response.ok) {
      const code = (response.status === 409 && isPlainObject(payload) && MESSAGES[payload.code])
        ? payload.code
        : (STATUS_CODES[response.status] || 'internal_error')
      const retryAfter = Number(response.headers?.get?.('Retry-After'))
      throw new CheckoutV2Error(code, {
        status: response.status,
        uncertain: unsafe && response.status >= 500,
        fieldErrors: fieldErrorsFrom(payload, fieldPaths),
        requestId: isPlainObject(payload) && typeof payload.request_id === 'string' ? payload.request_id : undefined,
        retryAfterMs: Number.isFinite(retryAfter) ? retryAfter * 1000 : undefined,
      })
    }
    return payload
  }

  async function bootstrapGuest() {
    if (csrfToken) return csrfToken
    if (!bootstrap) {
      bootstrap = request('POST', '/guest-sessions', { key: 'guest-session' })
        .then((session) => {
          if (!isPlainObject(session) || typeof session.csrf_token !== 'string' || !session.csrf_token) {
            throw new CheckoutV2Error('invalid_response')
          }
          csrfToken = session.csrf_token
          return csrfToken
        })
        .finally(() => { bootstrap = null })
    }
    return bootstrap
  }

  return {
    get identityEpoch() { return identityEpoch },
    bootstrapGuest,
    reset,
    get: (path, fieldPaths) => request('GET', path, { fieldPaths }),
    createDraft: (command, key) => request('POST', '/checkouts', { body: command, key }),
    readCheckout: (id) => request('GET', `/checkouts/${encodeURIComponent(id)}`),
    replaceSelection: (id, command, key) => request('PUT', `/checkouts/${encodeURIComponent(id)}/selection`, { body: command, key }),
    quote: (id, command, key) => request('POST', `/checkouts/${encodeURIComponent(id)}/quotes`, { body: command, key }),
    commit: (id, command, key) => request('POST', `/checkouts/${encodeURIComponent(id)}/commit`, {
      body: command, key, fieldPaths: (command.passengers || []).flatMap((_, index) => [
        `passengers.${index}.name`, `passengers.${index}.phone`, `passengers.${index}.email`,
        `passengers.${index}.cccd`, `passengers.${index}.cccd_address`, `passengers.${index}.pickup_location_id`,
      ]),
    }),
    cancel: (id, command, key) => request('POST', `/checkouts/${encodeURIComponent(id)}/cancel`, { body: command, key }),
    refreshPayment: (id, key) => request('POST', `/checkouts/${encodeURIComponent(id)}/payment-refresh`, { body: {}, key }),
    availability: (tripId) => request('GET', `/trips/${Number(tripId)}/availability`),
    // Recovery: start always answers generically; redeem consumes a one-use proof.
    startRecovery: (legacyToken, key) => request('POST', '/recovery/start', { body: { legacy_token: legacyToken }, key }),
    redeemRecovery: (token, key) => request('POST', '/recovery/redeem', { body: { token }, key }),
  }
}

export const CHECKOUT_V2_MESSAGES = MESSAGES
